import React, { useState, useEffect } from 'react';
import GenericModal from './GenericModal';
import { Edit2 } from 'lucide-react';
import { EsporaiInput, EsporaiTextarea } from 'components';
import { validateUrlRaw } from 'utils/validUrl.js';

const EditBrandModal = ({ isOpen, onClose, brand, onUpdateBrand }) => {
  const [brandName, setBrandName] = useState('');
  const [businessDetails, setBusinessDetails] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [urlError, setUrlError] = useState(null);

  useEffect(() => {
    if (isOpen && brand) {
      setBrandName(brand.name || '');
      setBusinessDetails(brand.business_details || '');
      setWebsiteUrl(brand.website_url || '');
      setUrlError(null);
    }
  }, [isOpen, brand]);

  const validateUrl = () => {
    if (!websiteUrl || !websiteUrl.trim()) {
      return { valid: true };
    }
    return validateUrlRaw(websiteUrl);
  };

  const handleSubmit = async () => {
    if (!brandName.trim()) {
      return;
    }

    const urlValidation = validateUrl();
    if (!urlValidation.valid) {
      setUrlError(urlValidation.error);
      return;
    }
    setUrlError(null);

    setIsUpdating(true);
    try {
      await onUpdateBrand({
        name: brandName.trim(),
        website_url: websiteUrl.trim() || undefined,
        business_details: businessDetails.trim() || undefined
      });
      onClose();
    } catch (error) {
      // El error ya se maneja en el componente padre
    } finally {
      setIsUpdating(false);
    }
  };

  const handleClose = () => {
    if (!isUpdating) {
      onClose();
    }
  };

  const isValid = brandName.trim().length > 0;
  const urlValidation = validateUrl();
  const submitDisabled = !isValid || !urlValidation.valid || isUpdating;

  if (!brand) return null;

  return (
    <GenericModal
      isOpen={isOpen}
      toggle={handleClose}
      titleSlot={
        <div className="flex items-center gap-3 w-full">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-500/20 border border-blue-400/30 backdrop-blur-md shadow-lg">
            <Edit2 className="w-5 h-5 text-blue-200" />
          </div>
          <div className="flex flex-col">
            <h2 className="text-2xl font-bold tracking-tight bg-gradient-to-r from-fuchsia-400 to-blue-500 bg-clip-text text-transparent">
              Editar Marca
            </h2>
            <span className="text-sm text-white/60 mt-0.5">
              Modifica el nombre, detalles del negocio y URL
            </span>
            <span className="mt-2 w-16 h-0.5 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full" />
          </div>
        </div>
      }
      description="Actualiza la información de tu marca"
      showLogo={false}
      closable={!isUpdating}
      showSubmitButton={true}
      showCloseButton={true}
      submitButtonText={isUpdating ? 'Guardando...' : 'Guardar'}
      closeButtonText="Cancelar"
      onSubmit={handleSubmit}
      onClose={handleClose}
      isSubmitting={isUpdating}
      submitDisabled={submitDisabled}
      width="480px"
      contentPadding="0 2rem 1.5rem 2rem"
    >
      <div className="space-y-4">
        <div>
          <label htmlFor="brandName" className="block text-sm font-semibold text-white/80 mb-2">
            Nombre de la Marca
          </label>
          <EsporaiInput
            id="brandName"
            type="text"
            value={brandName}
            onChange={setBrandName}
            placeholder="Nombre de tu negocio"
            disabled={isUpdating}
            maxLength={100}
            className="!bg-slate-900 !text-white placeholder-white/50 caret-white !border-white/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 [color-scheme:dark]"
            style={{ backgroundColor: '#0f172a', color: '#ffffff' }}
          />
        </div>
        <div>
          <label htmlFor="websiteUrl" className="block text-sm font-semibold text-white/80 mb-2">
            URL del sitio web
          </label>
          <EsporaiInput
            id="websiteUrl"
            type="text"
            value={websiteUrl}
            onChange={(val) => {
              setWebsiteUrl(val);
              setUrlError(null);
            }}
            placeholder="www.minegocio.com"
            error={urlError}
            disabled={isUpdating}
            className={`!bg-slate-900 !text-white placeholder-white/50 caret-white rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 [color-scheme:dark] ${
              urlError ? '!border-red-500' : '!border-white/30'
            }`}
            style={{ backgroundColor: '#0f172a', color: '#ffffff' }}
          />
        </div>
        <div>
          <label htmlFor="businessDetails" className="block text-sm font-semibold text-white/80 mb-2">
            Detalles de la marca
          </label>
          <EsporaiTextarea
            id="businessDetails"
            value={businessDetails}
            onChange={setBusinessDetails}
            placeholder="Detalles de tu negocio"
            disabled={isUpdating}
            rows={4}
            className="!bg-slate-900 !text-white placeholder-white/50 caret-white !border-white/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 [color-scheme:dark]"
            style={{ backgroundColor: '#0f172a', color: '#ffffff' }}
          />
        </div>
      </div>
    </GenericModal>
  );
};

export default EditBrandModal;
